import {GET} from '../modules/fetch.ts';
import {onUserEvent} from '../modules/worker.ts';
import {showElem, toggleElem} from '../utils/dom.ts';
import {parseDom} from '../utils.ts';
import {syncPullRequestFavicon} from '../modules/favicon-status.ts';

const batchDelay = 500;
const diffChangeKinds = new Set(['push', 'target-branch']);
const bannerChangeKinds = new Set([...diffChangeKinds, 'review', 'status']);

// Tells whether a part of the page that was left out of an update is still busy.
type BusyCheck = () => boolean;

// An element is in use while the user is typing, editing or choosing in it. Live updates leave it as it is.
export function isElemInUse(el: Element): boolean {
  if (el.contains(document.activeElement) && document.activeElement !== document.body) return true;
  if (el.querySelector('.edit-content-zone:not(.tw-hidden), .ui.dropdown.active')) return true;
  return Array.from(el.querySelectorAll('textarea')).some((textarea) => textarea.value !== textarea.defaultValue && textarea.getClientRects().length > 0);
}

const isTitleBeingEdited = () => document.querySelector('#issue-title-editor:not(.tw-hidden)') !== null;

// Ways to move a fresh render of a region into the page.
const swaps = {
  element: (oldEl: Element, newEl: Element) => oldEl.replaceWith(newEl),
  // keeps the element, so listeners bound to it survive
  content: (oldEl: Element, newEl: Element) => {
    for (const name of oldEl.getAttributeNames()) oldEl.removeAttribute(name);
    for (const attr of newEl.attributes) oldEl.setAttribute(attr.name, attr.value);
    oldEl.replaceChildren(...newEl.childNodes);
  },
  value: (oldEl: Element, newEl: Element) => {
    (oldEl as HTMLInputElement).value = (newEl as HTMLInputElement).value;
  },
};

type Region = {
  selector: string;
  swap: keyof typeof swaps;
  isBusy?: (el: Element) => boolean;
};

const conversationRegions: Region[] = [
  {selector: '#issue-title-display h1', swap: 'content', isBusy: isTitleBeingEdited},
  {selector: '#issue-title-editor input[name=title]', swap: 'value', isBusy: isTitleBeingEdited},
  {selector: '#pull-desc-display', swap: 'content', isBusy: isTitleBeingEdited},
  {selector: '.issue-title-meta .issue-state-label', swap: 'element'},
  {selector: '.pull.tabs.container > .pull.tabular.menu', swap: 'element'},
  {selector: '.issue-content-right', swap: 'element', isBusy: isElemInUse},
  {selector: '#status-button', swap: 'content'},
];

function syncRegion(doc: Document, {selector, swap, isBusy}: Region): BusyCheck | null {
  const oldEl = document.querySelector(selector);
  const newEl = doc.querySelector(selector);
  if (!oldEl || !newEl) return null;
  if (isBusy?.(oldEl)) return () => oldEl.isConnected && isBusy(oldEl);
  swaps[swap](oldEl, newEl);
  return null;
}

// Brings the timeline up to date and returns the items that were kept because they are in use.
export function syncTimeline(oldMain: Element, newMain: Element): Element[] {
  const oldEnd = oldMain.querySelector('#timeline-comments-end');
  const newEnd = newMain.querySelector('#timeline-comments-end');
  if (!oldEnd || !newEnd) return [];
  const itemsBefore = (end: Element) => {
    const siblings = Array.from(end.parentElement!.children);
    return siblings.slice(0, siblings.indexOf(end)).filter((el) => el.id);
  };
  const newItems = new Map(itemsBefore(newEnd).map((el) => [el.id, el]));
  const kept: Element[] = [];
  for (const oldItem of itemsBefore(oldEnd)) {
    const newItem = newItems.get(oldItem.id);
    newItems.delete(oldItem.id);
    if (isElemInUse(oldItem)) {
      kept.push(oldItem);
    } else if (!newItem) {
      oldItem.remove();
    } else if (oldItem.outerHTML !== newItem.outerHTML) {
      oldItem.replaceWith(newItem);
    }
  }
  oldEnd.before(...newItems.values());
  return kept;
}

function syncMergeBox(oldMain: Element, newMain: Element) {
  const oldBox = oldMain.querySelector('.pull-merge-box');
  const newBox = newMain.querySelector('.pull-merge-box');
  if (oldBox?.hasAttribute('data-user-interacted')) return;
  if (oldBox && newBox) oldBox.replaceWith(newBox);
  if (oldBox && !newBox) oldBox.remove();
  if (!oldBox && newBox) oldMain.querySelector('#timeline-comments-end')?.after(newBox);
}

// A fresh render labels the close and reopen button for an empty comment, so match the label to the comment being typed.
function syncStatusButtonLabel() {
  const button = document.querySelector('#status-button');
  const label = button?.querySelector('.status-button-text');
  if (!button || !label) return;
  const hasComment = Boolean(document.querySelector<HTMLTextAreaElement>('#comment-form textarea')?.value.trim());
  label.textContent = button.getAttribute(hasComment ? 'data-status-and-comment' : 'data-status');
}

// Updates the conversation from a fresh render, and returns checks for the parts it left alone because they are in use.
async function refreshConversation(): Promise<BusyCheck[]> {
  const resp = await GET(window.location.href);
  if (!resp.ok) return [];
  const doc = parseDom(await resp.text(), 'text/html');
  syncPullRequestFavicon(doc);
  const oldMain = document.querySelector('.issue-content-left');
  const newMain = doc.querySelector('.issue-content-left');
  if (!oldMain || !newMain) return [];

  const busyChecks = conversationRegions.map((region) => syncRegion(doc, region)).filter((check) => check !== null);
  for (const item of syncTimeline(oldMain, newMain)) busyChecks.push(() => item.isConnected && isElemInUse(item));
  syncMergeBox(oldMain, newMain);
  syncStatusButtonLabel();
  return busyChecks;
}

function showBanner(container: Element, kinds: Set<string>) {
  const banner = container.querySelector('.pull-live-banner')!;
  const isPush = [...kinds].some((kind) => diffChangeKinds.has(kind));
  for (const text of banner.querySelectorAll('[data-live-kind]')) toggleElem(text, (text.getAttribute('data-live-kind') === 'push') === isPush);
  showElem(banner);
}

// Returns a trigger that collects change kinds and runs `task` once per burst of triggers.
// The task never runs twice at once. Page content waits while the tab is hidden.
function batchWhileVisible(task: (kinds: Set<string>) => Promise<void>, runWhenHidden = false): (kind?: string) => void {
  let pendingKinds = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let running = false;
  const run = async () => {
    if (running || (document.hidden && !runWhenHidden) || !pendingKinds.size) return;
    const kinds = pendingKinds;
    pendingKinds = new Set();
    running = true;
    try {
      await task(kinds);
    } catch (err) {
      console.error('pull live update failed', err);
    } finally {
      running = false;
      if (pendingKinds.size) timer = setTimeout(run, batchDelay);
    }
  };
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) timer = setTimeout(run, batchDelay);
  });
  return (kind) => {
    if (kind) pendingKinds.add(kind);
    clearTimeout(timer);
    timer = setTimeout(run, batchDelay);
  };
}

// Listens for changes to this pull request on the user events WebSocket.
// The conversation updates in place. The commits and files views show a banner, because replacing a diff would lose review drafts.
export function initRepoPullLiveUpdate(container: HTMLElement) {
  const issueId = Number(container.getAttribute('data-issue-id'));
  const isConversation = container.getAttribute('data-pull-view') === 'conversation';
  container.querySelector('.pull-live-reload')!.addEventListener('click', () => window.location.reload());

  const updateFavicon = batchWhileVisible(async () => {
    if (isConversation && !document.hidden) return;
    const resp = await GET(`${container.getAttribute('data-pull-link')!}/merge_box`);
    if (resp.ok) syncPullRequestFavicon(parseDom(await resp.text(), 'text/html'));
  }, true);
  if (isConversation) syncPullRequestFavicon(document);
  else updateFavicon('initial');

  let busyChecks: BusyCheck[] = [];
  const update = batchWhileVisible(async (kinds) => {
    if (isConversation) {
      busyChecks = await refreshConversation();
    } else if ([...kinds].some((kind) => bannerChangeKinds.has(kind))) {
      showBanner(container, kinds);
    }
  });

  // Parts left alone while in use are updated once the user leaves them.
  for (const eventName of ['focusout', 'click', 'submit']) {
    document.addEventListener(eventName, () => {
      // let the click or submit change the page before checking
      setTimeout(() => {
        if (!busyChecks.length || busyChecks.every((isBusy) => isBusy())) return;
        busyChecks = [];
        update('kept');
      }, 0);
    });
  }

  onUserEvent('issue-updated', (msg) => {
    if (msg.eventData.issueID !== issueId) return;
    update(msg.eventData.kind);
    if (['checks', 'status', 'push', 'target-branch'].includes(msg.eventData.kind)) {
      updateFavicon(msg.eventData.kind);
    }
  });
  let wasConnected = false;
  onUserEvent('worker-connected', () => {
    if (wasConnected) {
      update('reconnect'); // events sent while the socket was down are lost
      updateFavicon('reconnect');
    }
    wasConnected = true;
  });
}
